import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  type InputSignal,
  type OutputEmitterRef,
  type Signal,
  type WritableSignal,
} from '@angular/core';
import {
  UiBottomSheetComponent,
  UiButtonComponent,
  UiCardComponent,
  UiCheckboxRowComponent,
  UiInputComponent,
  UiInlineMessageComponent,
  UiSuccessStateComponent,
} from '@nexus/frontend-shared-ui';
import type { WishlistDetailReservationDraft } from '../../types/wishlist-detail-reservation-draft.interface';
import { WISHLIST_DETAIL_RESERVATION_EMAIL_PATTERN } from './wishlist-detail-reservation-drawer.constants';
import { WISHLIST_RESERVATION_TERMS_URL } from '../../tokens/wishlist-reservation-terms-url.token';

@Component({
  selector: 'wishlist-detail-reservation-drawer',
  imports: [
    UiBottomSheetComponent,
    UiButtonComponent,
    UiCardComponent,
    UiCheckboxRowComponent,
    UiInlineMessageComponent,
    UiInputComponent,
    UiSuccessStateComponent,
  ],
  templateUrl: './wishlist-detail-reservation-drawer.component.html',
  styleUrl: './wishlist-detail-reservation-drawer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WishlistDetailReservationDrawerComponent {
  protected readonly termsUrl: string | null = inject(WISHLIST_RESERVATION_TERMS_URL);
  protected readonly isQaScenario: boolean = this.termsUrl === 'https://qa.molibra.pl/zasady-rezerwacji.html';
  public readonly open: InputSignal<boolean> = input<boolean>(false);
  public readonly itemTitle: InputSignal<string | null> = input<string | null>(null);
  public readonly beneficiaryLabel: InputSignal<string | null> = input<string | null>(null);
  public readonly pending: InputSignal<boolean> = input<boolean>(false);
  public readonly success: InputSignal<boolean> = input<boolean>(false);
  public readonly confirmed: InputSignal<boolean> = input(false);
  public readonly feedback: InputSignal<string | null> = input<string | null>(null);
  public readonly resent: OutputEmitterRef<void> = output<void>();
  public readonly correctionRequested: OutputEmitterRef<void> = output<void>();
  public readonly errorMessage: InputSignal<string | null> = input<string | null>(null);
  public readonly closed: OutputEmitterRef<void> = output<void>();
  public readonly submitted: OutputEmitterRef<WishlistDetailReservationDraft> =
    output<WishlistDetailReservationDraft>();

  protected readonly email: WritableSignal<string> = signal<string>('');
  protected readonly acceptedTerms: WritableSignal<boolean> = signal<boolean>(false);
  protected readonly formDisabled: Signal<boolean> = computed((): boolean => this.pending() || this.success());
  protected readonly emailError: Signal<string | null> = computed((): string | null => {
    const email: string = this.email().trim();

    if (!email || WISHLIST_DETAIL_RESERVATION_EMAIL_PATTERN.test(email)) {
      return null;
    }

    return 'Podaj poprawny email.';
  });
  protected readonly canSubmit: Signal<boolean> = computed((): boolean => {
    return (
      WISHLIST_DETAIL_RESERVATION_EMAIL_PATTERN.test(this.email().trim()) &&
      this.termsUrl !== null &&
      this.acceptedTerms() &&
      !this.formDisabled()
    );
  });
  protected readonly title: Signal<string> = computed((): string => {
    if (this.success()) {
      return this.confirmed() ? 'Prezent zarezerwowany' : 'Czeka na potwierdzenie';
    }

    return 'Zarezerwuj prezent';
  });
  protected readonly description: Signal<string> = computed((): string => {
    if (this.success()) {
      return this.feedback() ?? 'Zgłoszenie przyjęte. Rezerwacja nie jest jeszcze aktywna. Do potwierdzenia potrzebny będzie link z wiadomości.';
    }

    return 'Podaj email i zaakceptuj regulamin. Rezerwacja będzie aktywna dopiero po potwierdzeniu przyciskiem na stronie.';
  });
  private readonly resetClosedDraft = effect((): void => {
    if (!this.open()) {
      this.resetDraft();
    }
  });

  protected handleEmailChanged(value: string): void {
    this.email.set(value);
  }

  protected handleTermsChanged(value: boolean): void {
    this.acceptedTerms.set(value);
  }

  protected handleClosed(): void {
    if (this.pending()) {
      return;
    }
    this.resetDraft();
    this.closed.emit();
  }

  protected handleSubmit(): void {
    if (!this.canSubmit()) {
      return;
    }

    this.submitted.emit({
      email: this.email().trim(),
      acceptedTerms: this.acceptedTerms(),
    });
  }

  private resetDraft(): void {
    this.email.set('');
    this.acceptedTerms.set(false);
  }
}
